import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { Route, RouterModule } from '@angular/router';
import { BusinessCardAuthenticatedGuard } from '@planone/bau365/data-access-business-card';
import { FairExistsGuard } from '@planone/bau365/data-access-fair-data';
import { redirectToTickets as RedirectToTicketsGuard } from '@planone/bau365/data-access-login-portal';
import { QrCodeScannerEnabledGuard } from '@planone/bau365/data-access-qrcode-scanner';
import {
  Bau365DataAccessUserModule,
  NewsfeedEnabledGuard,
  ProfileCompletedGuard,
  ProgramEnabledGuard,
  SearchEnabledGuard,
  WatchlistEnabledGuard,
} from '@planone/bau365/data-access-user';
// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries
import { RedirectToNewsfeedGuard } from '@planone/bau365/feature-newsfeed';
import { Bau365UiShellModule, PlatformHelper } from '@planone/bau365/ui-shell';
import { BauInsightsAuthenticatedGuard } from '@planone/shared/util-auth-guards';

import { ShellComponent } from './shell/shell.component';

export const bau365FeatureShellRoutes: Route[] = [
  {
    path: '',
    component: ShellComponent,
    children: [
      {
        path: '',
        canActivate: PlatformHelper.allowServerRendering([ProfileCompletedGuard, RedirectToNewsfeedGuard]),
        loadChildren: () => import('@planone/bau365/feature-home').then((module) => module.Bau365FeatureHomeModule),
      },
      {
        path: 'login/portal',
        canActivate: PlatformHelper.allowServerRendering([RedirectToTicketsGuard]),
        loadChildren: () =>
          import('@planone/bau365/feature-login-portal').then((module) => module.Bau365FeatureLoginPortalModule),
      },
      {
        path: 'logout',
        canActivate: PlatformHelper.allowServerRendering([ProfileCompletedGuard, RedirectToNewsfeedGuard]),
        loadChildren: () => import('@planone/bau365/feature-logout').then((module) => module.Bau365FeatureLogoutModule),
      },
      {
        path: 'legal',
        loadChildren: () => import('@planone/bau365/feature-legal').then((module) => module.Bau365FeatureLegalModule),
      },
      {
        path: 'profile',
        canActivate: [BauInsightsAuthenticatedGuard, ProfileCompletedGuard],
        runGuardsAndResolvers: 'always',
        loadChildren: () =>
          import('@planone/bau365/feature-user-profile').then((module) => module.Bau365FeatureUserProfileModule),
      },
      {
        path: 'profilecompletion',
        canActivate: [BauInsightsAuthenticatedGuard],
        runGuardsAndResolvers: 'always',
        loadChildren: () =>
          import('@planone/bau365/feature-user-profile-completion').then(
            (module) => module.Bau365FeatureUserProfileCompletionModule
          ),
      },
      {
        path: 'contact',
        loadChildren: () =>
          import('@planone/bau365/feature-contact').then((module) => module.Bau365FeatureContactModule),
      },
      {
        path: 'watchlist',
        canActivate: PlatformHelper.allowServerRendering([
          BauInsightsAuthenticatedGuard,
          ProfileCompletedGuard,
          WatchlistEnabledGuard,
        ]),
        loadChildren: () =>
          import('@planone/bau365/feature-watchlist').then((module) => module.Bau365FeatureWatchlistModule),
      },
      {
        path: 'newsfeed',
        canActivate: PlatformHelper.allowServerRendering([
          BauInsightsAuthenticatedGuard,
          ProfileCompletedGuard,
          NewsfeedEnabledGuard,
        ]),
        loadChildren: () =>
          import('@planone/bau365/feature-newsfeed').then((module) => module.Bau365FeatureNewsfeedModule),
      },
      {
        path: 'business-card/:uuid',
        canActivate: PlatformHelper.allowServerRendering([BusinessCardAuthenticatedGuard]),
        loadChildren: () =>
          import('@planone/bau365/feature-business-card').then((module) => module.Bau365FeatureBusinessCardModule),
      },
      {
        path: 'scanner',
        canActivate: PlatformHelper.allowServerRendering([
          QrCodeScannerEnabledGuard,
          BauInsightsAuthenticatedGuard,
          ProfileCompletedGuard,
        ]),
        loadChildren: () =>
          import('@planone/bau365/feature-qrcode-scanner').then((module) => module.Bau365FeatureQrcodeScannerModule),
      },
      {
        path: 'tickets',
        canActivate: PlatformHelper.allowServerRendering([BauInsightsAuthenticatedGuard, ProfileCompletedGuard]),
        loadChildren: () => import('@planone/bau365/feature-tickets').then((module) => module.ROUTES),
      },
      {
        path: 'address-book',
        canActivate: PlatformHelper.allowServerRendering([BauInsightsAuthenticatedGuard]),
        loadChildren: () =>
          import('@planone/bau365/feature-address-book').then((module) => module.Bau365FeatureAddressBookModule),
      },
      {
        path: 'fairs',
        canActivate: PlatformHelper.allowServerRendering([ProfileCompletedGuard, FairExistsGuard]),
        children: [
          {
            path: '',
            pathMatch: 'full',
            loadChildren: () =>
              import('@planone/bau365/feature-fairs').then((module) => module.Bau365FeatureFairsModule),
          },
          {
            path: ':fair_id',
            pathMatch: 'full',
            redirectTo: ':fair_id/exhibitors',
          },
          {
            path: ':fair_id/exhibitors',
            loadChildren: () =>
              import('@planone/bau365/feature-exhibitors').then((module) => module.Bau365FeatureExhibitorsModule),
          },
          {
            path: ':fair_id/products',
            canActivate: PlatformHelper.allowServerRendering([ProfileCompletedGuard]),
            loadChildren: () =>
              import('@planone/bau365/feature-products').then((module) => module.Bau365FeatureProductsModule),
          },
          {
            path: ':fair_id/posts',
            canActivate: PlatformHelper.allowServerRendering([ProfileCompletedGuard]),
            loadChildren: () =>
              import('@planone/bau365/feature-posts').then((module) => module.Bau365FeaturePostsModule),
          },
          {
            path: ':fair_id/events',
            loadChildren: () =>
              import('@planone/bau365/feature-events').then((module) => module.Bau365FeatureEventsModule),
          },
          {
            path: ':fair_id/search',
            canActivate: PlatformHelper.allowServerRendering([ProfileCompletedGuard, SearchEnabledGuard]),
            loadChildren: () =>
              import('@planone/bau365/feature-search').then((module) => module.Bau365FeatureSearchModule),
          },
          {
            path: ':fair_id/hall-plan',
            loadChildren: () =>
              import('@planone/bau365/feature-hall-plan').then((module) => module.Bau365FeatureHallPlanModule),
          },
          {
            path: ':fair_id/program',
            canActivate: PlatformHelper.allowServerRendering([ProgramEnabledGuard]),
            loadChildren: () =>
              import('@planone/bau365/feature-program').then((module) => module.Bau365FeatureProgramModule),
          },
        ],
      },
      {
        path: 'download',
        loadChildren: () => import('@planone/bau365/feature-export').then((module) => module.Bau365FeatureExportModule),
      },
      {
        path: 'scan',
        loadChildren: () => import('@planone/bau365/feature-scan').then((module) => module.ROUTES),
      },
      {
        path: '**',
        pathMatch: 'full',
        loadChildren: () => import('@planone/bau365/feature-errors').then((module) => module.Bau365FeatureErrorsModule),
      },
    ],
  },
];

@NgModule({
  imports: [
    CommonModule,
    RouterModule.forRoot(bau365FeatureShellRoutes, {
      anchorScrolling: 'enabled',
      scrollOffset: [0, 128],
      scrollPositionRestoration: 'enabled',
      onSameUrlNavigation: 'reload',
    }),
    MatSnackBarModule,
    Bau365UiShellModule,
    Bau365DataAccessUserModule,
  ],
  exports: [RouterModule],
  declarations: [ShellComponent],
})
export class Bau365FeatureShellModule {}
